// Paid-subscription country eligibility (B2B legal brief 2026-06-11).
//
// Policy:
//   - The FREE demo is open to anyone, anywhere — this list does NOT gate it.
//   - PAID subscriptions are UK + EU-27 ONLY. UK is charged 20% VAT; EU buyers
//     must enter a valid EU VAT number at Stripe checkout (reverse charge, 0%)
//     which keeps EcoIntel out of EU OSS registration.
//   - Anyone outside UK/EU is shown a "register interest" path instead of
//     checkout (handled in the Subscription component).
//
// VAT validation is performed by Stripe/VIES at checkout — there is no in-app
// VIES client. Greece's ISO country code is GR while its VAT prefix is EL;
// Stripe handles the prefix, so we only ever compare ISO country codes here.

// EU-27 ISO 3166-1 alpha-2 country codes.
export const EU_COUNTRY_CODES = Object.freeze([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

// Display list (GB first, then EU-27 alphabetically by name) for the
// eligibility selector on the checkout page.
export const SUBSCRIPTION_COUNTRIES = Object.freeze([
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
]);

const EU_SET = new Set(EU_COUNTRY_CODES);
const ELIGIBLE_SET = new Set(['GB', ...EU_COUNTRY_CODES]);

export function normalizeCountryCode(code) {
  return (code || '').trim().toUpperCase();
}

/** True if the country is eligible for a PAID subscription (UK + EU-27). */
export function isSubscriptionEligibleCountry(code) {
  return ELIGIBLE_SET.has(normalizeCountryCode(code));
}

/** True if the country is in the EU-27 (i.e. a VAT number is required). */
export function isEuCountry(code) {
  return EU_SET.has(normalizeCountryCode(code));
}

// Best-effort map from a free-text country name (as captured in the profile
// address field) to an ISO code, so we can pre-select the eligibility dropdown
// without forcing re-entry. Returns '' when we can't confidently match — the
// user then picks explicitly. Intentionally conservative: only exact (case/
// punctuation-insensitive) name or code matches resolve.
const NAME_TO_CODE = (() => {
  const map = new Map();
  for (const { code, name } of SUBSCRIPTION_COUNTRIES) {
    map.set(name.toLowerCase(), code);
    map.set(code.toLowerCase(), code);
  }
  // Common alternative spellings for the UK.
  ['uk', 'united kingdom', 'great britain', 'england', 'scotland', 'wales', 'northern ireland']
    .forEach((alias) => map.set(alias, 'GB'));
  return map;
})();

export function guessCountryCodeFromName(value) {
  if (!value) return '';
  const key = String(value).trim().toLowerCase();
  return NAME_TO_CODE.get(key) || '';
}
